import { create } from 'zustand';
import {
  getTaxonomy,
  createCategory,
  updateCategory,
  deleteCategory,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory,
  getCourses,
  getCourseStructure,
  getCourseHistory,
  transitionLifecycle,
  TaxonomyCategory,
  GovernanceCourse,
  CourseStructure,
  LifecycleHistoryEntry,
  CourseStatus,
  LifecycleAction,
} from '@/services/PagesServices/ContentGovernanceService';

type ActionResult = { success: boolean; message: string };

interface ContentGovernanceState {
  taxonomy: TaxonomyCategory[];
  courses: GovernanceCourse[];
  countsByStatus: Record<CourseStatus, number> | null;
  structure: CourseStructure | null;
  history: LifecycleHistoryEntry[];

  loadingTaxonomy: boolean;
  loadingCourses: boolean;
  loadingStructure: boolean;
  saving: boolean;
  error: string | null;

  fetchTaxonomy: () => Promise<void>;
  addCategory: (name: string, description?: string) => Promise<ActionResult>;
  editCategory: (id: number, name: string, description?: string) => Promise<ActionResult>;
  removeCategory: (id: number) => Promise<ActionResult>;
  addSubcategory: (categoryId: number, name: string, description?: string) => Promise<ActionResult>;
  editSubcategory: (id: number, name: string, description?: string) => Promise<ActionResult>;
  removeSubcategory: (id: number) => Promise<ActionResult>;

  fetchCourses: (status?: CourseStatus | '') => Promise<void>;
  fetchStructure: (courseId: number) => Promise<void>;
  fetchHistory: (courseId: number) => Promise<void>;
  transition: (courseId: number, action: LifecycleAction, note?: string) => Promise<ActionResult>;
}

const useContentGovernanceStore = create<ContentGovernanceState>((set, get) => ({
  taxonomy: [],
  courses: [],
  countsByStatus: null,
  structure: null,
  history: [],

  loadingTaxonomy: false,
  loadingCourses: false,
  loadingStructure: false,
  saving: false,
  error: null,

  fetchTaxonomy: async () => {
    set({ loadingTaxonomy: true, error: null });
    const res = await getTaxonomy();
    if (res.success) set({ taxonomy: res.data || [], loadingTaxonomy: false });
    else set({ error: res.error?.message ?? 'Failed to load taxonomy', loadingTaxonomy: false });
  },

  addCategory: async (name, description) => {
    set({ saving: true });
    const res = await createCategory(name, description);
    set({ saving: false });
    if (res.success) { get().fetchTaxonomy(); return { success: true, message: res.message ?? 'Category created' }; }
    return { success: false, message: res.error?.message ?? 'Failed' };
  },
  editCategory: async (id, name, description) => {
    set({ saving: true });
    const res = await updateCategory(id, name, description);
    set({ saving: false });
    if (res.success) { get().fetchTaxonomy(); return { success: true, message: res.message ?? 'Category updated' }; }
    return { success: false, message: res.error?.message ?? 'Failed' };
  },
  removeCategory: async (id) => {
    set({ saving: true });
    const res = await deleteCategory(id);
    set({ saving: false });
    if (res.success) { get().fetchTaxonomy(); return { success: true, message: res.message ?? 'Category deleted' }; }
    return { success: false, message: res.error?.message ?? 'Failed' };
  },
  addSubcategory: async (categoryId, name, description) => {
    set({ saving: true });
    const res = await createSubcategory(categoryId, name, description);
    set({ saving: false });
    if (res.success) { get().fetchTaxonomy(); return { success: true, message: res.message ?? 'Subcategory created' }; }
    return { success: false, message: res.error?.message ?? 'Failed' };
  },
  editSubcategory: async (id, name, description) => {
    set({ saving: true });
    const res = await updateSubcategory(id, name, description);
    set({ saving: false });
    if (res.success) { get().fetchTaxonomy(); return { success: true, message: res.message ?? 'Subcategory updated' }; }
    return { success: false, message: res.error?.message ?? 'Failed' };
  },
  removeSubcategory: async (id) => {
    set({ saving: true });
    const res = await deleteSubcategory(id);
    set({ saving: false });
    if (res.success) { get().fetchTaxonomy(); return { success: true, message: res.message ?? 'Subcategory deleted' }; }
    return { success: false, message: res.error?.message ?? 'Failed' };
  },

  fetchCourses: async (status) => {
    set({ loadingCourses: true, error: null });
    const res = await getCourses(status);
    if (res.success) set({ courses: res.data || [], countsByStatus: res.countsByStatus ?? null, loadingCourses: false });
    else set({ error: res.error?.message ?? 'Failed to load courses', loadingCourses: false });
  },

  fetchStructure: async (courseId) => {
    set({ loadingStructure: true, structure: null });
    const res = await getCourseStructure(courseId);
    set({ structure: res.success ? (res.data ?? null) : null, loadingStructure: false });
  },

  fetchHistory: async (courseId) => {
    const res = await getCourseHistory(courseId);
    set({ history: res.success ? (res.data || []) : [] });
  },

  transition: async (courseId, action, note) => {
    set({ saving: true });
    const res = await transitionLifecycle(courseId, action, note);
    set({ saving: false });
    if (res.success) {
      await get().fetchCourses();
      return { success: true, message: res.message ?? 'Updated' };
    }
    return { success: false, message: res.error?.message ?? 'Transition failed' };
  },
}));

export default useContentGovernanceStore;
